import { useMemo } from 'react';
import {
  ADMIN_TABS,
  groupTabsByCategory,
  type AdminCategoryGroup,
  type AdminTab,
} from './adminTabs.config';
import { useAuth } from '@/contexts/AuthContext';
import { useEffectiveRoles } from '@/lib/previewRole';
import { useHiddenFeatures } from '@/hooks/useHiddenFeatures';
import { isFeatureHidden, adminTabFeatureId } from '@/config/featureIds';

export interface VisibleAdminTabs {
  /** Tabs the user's role permits AND that are not hidden by the kill-switch. */
  visibleTabs: AdminTab[];
  /**
   * Tabs the user's role permits (including hidden ones). Used to match the
   * active route so a direct nav to a hidden admin route highlights nothing
   * rather than mis-selecting the first tab.
   */
  roleTabs: AdminTab[];
  /** Visible tabs grouped by category, in `ADMIN_CATEGORIES` order. */
  groups: AdminCategoryGroup[];
}

/**
 * Single source of truth for which admin tabs the current user can see and how
 * they group into categories. Shared by `AdminLayout` (grouped tab strip) and
 * `AdminLandingPage` (category grid) so the role + hidden-feature filtering is
 * written once.
 */
export function useVisibleAdminTabs(): VisibleAdminTabs {
  const { user } = useAuth();
  // Effective, not real: previewing a role has to answer for THAT role here
  // too, or "View the site as Marketing" keeps showing admin-locked tabs.
  const effectiveRoles = useEffectiveRoles(user?.roles);
  const { hiddenFeatures } = useHiddenFeatures();

  return useMemo(() => {
    const roles = new Set(effectiveRoles ?? []);
    // A tab with no `roles` is governed by Feature Visibility alone; a tab that
    // names them is hard-locked and no setting can widen it (see AdminTab).
    const roleTabs = ADMIN_TABS.filter((tab) => !tab.roles || tab.roles.some((r) => roles.has(r)));
    const visibleTabs = roleTabs.filter(
      (tab) => !isFeatureHidden(adminTabFeatureId(tab.value), hiddenFeatures),
    );
    return { visibleTabs, roleTabs, groups: groupTabsByCategory(visibleTabs) };
  }, [effectiveRoles, hiddenFeatures]);
}
